import { useEffect, useMemo, useState } from "react";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { dispatchInventoryChangelogRefresh } from "@/lib/manufacturing/inventoryChangelogRefreshEvent";
import { postManufacturingRecipeCostEntry } from "@/lib/manufacturingClient";
import {
  clearRecipeCountOfflineDraft,
  loadRecipeCountOfflineDraft,
  saveRecipeCountOfflineDraft,
  type RecipeCountDraftLine,
} from "@/lib/manufacturing/recipeCountOfflineDraft";
import {
  matchSpokenLinesToCatalog,
  parseSpokenInventoryLines,
  type CatalogMatch,
} from "@/lib/manufacturing/spokenCatalogMatch";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";
import { inputClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingDictatePad } from "./ManufacturingDictatePad";
import { SpokenCatalogMatchList } from "./SpokenCatalogMatchList";

export function RecipeCountDictatePanel(props: {
  recipes: ReadonlyArray<MfgRecipeSummary>;
  enabled: boolean;
  onPosted: (msg: string) => void;
}) {
  const orgId = useScopedOrganizationId() ?? "";
  const [lines, setLines] = useState<RecipeCountDraftLine[]>([]);
  const [heard, setHeard] = useState<CatalogMatch[]>([]);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [padReset, setPadReset] = useState(0);

  const catalog = useMemo(
    () =>
      props.recipes
        .filter((r) => r.is_active)
        .map((r) => ({ id: r.id, name: r.name, code: r.code })),
    [props.recipes],
  );

  useEffect(() => {
    if (!orgId) return;
    const draft = loadRecipeCountOfflineDraft(orgId);
    if (draft) setLines(draft.lines);
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    if (lines.length === 0) {
      clearRecipeCountOfflineDraft(orgId);
      return;
    }
    saveRecipeCountOfflineDraft({
      version: 1,
      saved_at: new Date().toISOString(),
      organization_id: orgId,
      lines,
    });
  }, [lines, orgId]);

  function handleExtract(transcript: string) {
    const spoken = parseSpokenInventoryLines(transcript);
    if (spoken.length === 0) {
      setLines([]);
      setHeard([]);
      setError(
        "Couldn't find a quantity and recipe name. Try “quantity 10 item 10 wide utility trusses”.",
      );
      return;
    }
    const matches = matchSpokenLinesToCatalog(spoken, catalog);
    setHeard(matches);
    setLines(
      matches.map((m) => ({
        key: crypto.randomUUID(),
        recipe_id: m.candidate?.id ?? null,
        recipe_name: m.candidate?.name ?? m.spoken.name,
        qty: String(m.spoken.qty),
        unit_cost: "",
        match: m.status,
      })),
    );
    setError(null);
  }

  async function postAll() {
    const ready = lines.filter((l) => l.recipe_id && Number(l.qty) !== 0);
    if (ready.length === 0) {
      setError("Match at least one recipe and enter a non-zero qty.");
      return;
    }
    setWorking(true);
    setError(null);
    let posted = 0;
    for (const line of ready) {
      const qty = Number(line.qty);
      const unitCost = line.unit_cost.trim() === "" ? 0 : Number(line.unit_cost);
      if (!Number.isFinite(qty) || !Number.isFinite(unitCost) || unitCost < 0) {
        setError(`Check qty/cost on ${line.recipe_name}.`);
        setWorking(false);
        return;
      }
      const r = await postManufacturingRecipeCostEntry({
        recipe_id: line.recipe_id as string,
        entry_type: "adjustment",
        quantity: qty,
        unit_cost: unitCost,
        notes: "Dictated recipe count",
      });
      if (!r.ok) {
        setError(r.error);
        setWorking(false);
        return;
      }
      posted += 1;
    }
    setWorking(false);
    setLines([]);
    setHeard([]);
    if (orgId) clearRecipeCountOfflineDraft(orgId);
    props.onPosted(`Posted ${posted} recipe count line${posted === 1 ? "" : "s"} (saved locally until this succeeded).`);
    dispatchInventoryChangelogRefresh();
    setPadReset((n) => n + 1);
  }

  return (
    <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50/40 p-3 dark:border-violet-900 dark:bg-violet-950/20">
      <div className="text-xs font-semibold text-gray-800 dark:text-zinc-100">
        Dictate recipe counts
      </div>
      <p className="mb-2 text-[11px] text-gray-500">
        “quantity 40 item trusses, quantity 12 item gable ends.” Quantity then item keeps counts
        from blending into names. Edits stay on this device until you post.
      </p>
      <ManufacturingDictatePad
        disabled={!props.enabled || working}
        placeholder="quantity 40 item trusses…"
        extractLabel="Match recipes"
        resetKey={padReset}
        onComplete={handleExtract}
      />
      {error ? <p className="mt-2 text-[11px] text-red-700">{error}</p> : null}
      <SpokenCatalogMatchList matches={heard} />
      {lines.length > 0 ? (
        <div className="mt-2 space-y-2">
          {lines.map((line) => (
            <div key={line.key} className="grid grid-cols-3 gap-2 text-[11px]">
              <div className="col-span-3 font-medium">
                {line.recipe_name}
                {line.match === "unmatched" ? " — unmatched" : ` · ${line.match}`}
              </div>
              <input
                className={inputClass}
                value={line.qty}
                onChange={(e) =>
                  setLines((prev) =>
                    prev.map((l) => (l.key === line.key ? { ...l, qty: e.target.value } : l)),
                  )
                }
              />
              <input
                className={inputClass}
                placeholder="Unit cost"
                value={line.unit_cost}
                onChange={(e) =>
                  setLines((prev) =>
                    prev.map((l) =>
                      l.key === line.key ? { ...l, unit_cost: e.target.value } : l,
                    ),
                  )
                }
              />
            </div>
          ))}
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            disabled={!props.enabled || working}
            onClick={() => void postAll()}
          >
            {working ? "Posting…" : "Post matched counts"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
